/**
 * windowRouter — Per-window router (Option C).
 *
 * Every window holds a canonical `route = { appType, params }`. This module is
 * the single write-path for all window navigation. `window.entityId`, `title`,
 * and `appType` are derived projections of the current route, written only by
 * `reconcileWindowIdentity`.
 *
 * Debug: `?debug_window_router=1` or `localStorage.setItem('debug_window_router','1')`
 *   → verbose [WINDOW_ROUTER] logs + `window.__windowRouter` global surface.
 *   → `window.__windowRouter.stats` — lifetime counters (reconciles, drifts, etc.)
 *   → `window.__windowRouter.assert()` — invariant check across all open windows
 *
 * Migration: legacy mutators (`navigateWindow`, `clearWindowEntityId`,
 * `navigateBack`, `navigateForward` in spaceActions.ts) are rewired to call
 * through here. Legacy fields (`navigationHistory`, `navigationIndex`,
 * `entityId`, `entityLlmId`, `title`) are kept mirrored during the transition
 * so existing code keeps working untouched.
 */

import { createFileLogger } from '../utils/logger';
import { entityRegistry } from './entityRegistry';
import { upsertEntity } from './entityMutations';
import { getAppTitleByType } from '../components/Space/utils/windowUtils';
import { useAppRegistryStore } from './appRegistryStore';
import useSpaceStore from './spaceStore';
import { deriveRouteFromWindow, canonicalizeWindowAppType } from '../types/windowRoute';
import type { WindowRoute, WindowRouteParams } from '../types/windowRoute';

const routerLog = createFileLogger('WINDOW_ROUTER', { enabled: false });

// ─── History cap ──────────────────────────────────────────────────────────────

const MAX_ROUTE_HISTORY = 50;

// ─── Stats (always-on counters, surfaced via window.__windowRouter.stats) ─────

interface RouterStats {
  reconciles: number;
  drifts: number;
  clears: number;
  pinnedBlocks: number;
  mountClearsSkipped: number;
  restFetches: number;
  historyTrims: number;
}

const routerStats: RouterStats = {
  reconciles: 0,
  drifts: 0,
  clears: 0,
  pinnedBlocks: 0,
  mountClearsSkipped: 0,
  restFetches: 0,
  historyTrims: 0,
};

// ─── Debug flag ───────────────────────────────────────────────────────────────

function isDebugEnabled(): boolean {
  if (typeof window === 'undefined') return false;
  return (
    localStorage.getItem('debug_window_router') === '1' ||
    new URLSearchParams(window.location.search).get('debug_window_router') === '1'
  );
}

function dbg(msg: string, data?: Record<string, unknown>): void {
  if (isDebugEnabled()) {
    routerLog.log(`[WINDOW_ROUTER] ${msg}`, data ?? '');
    if (typeof window !== 'undefined') {
      console.debug(`[WINDOW_ROUTER] ${msg}`, data ?? '');
    }
  }
}

function warn(msg: string, data?: Record<string, unknown>): void {
  routerLog.warn(`[WINDOW_ROUTER] ${msg}`, data ?? '');
  if (isDebugEnabled() && typeof window !== 'undefined') {
    console.warn(`[WINDOW_ROUTER] ${msg}`, data ?? '');
  }
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

// Re-export for back-compat with callers that imported deriveRouteFromWindow from here.
export { deriveRouteFromWindow } from '../types/windowRoute';

/** Get the current spaceStore state. Direct call — no lazy loading needed. */
function getSpaceStore() {
  return useSpaceStore.getState();
}

/**
 * Returns true if the window carries a permanent entity identity that must not
 * be overwritten (AppInstance-backed sidebar entries). Defense-in-depth: checks
 * both the explicit flag (set at creation) and a live registry lookup so that
 * pre-existing windows persisted before the flag was added are also protected.
 */
function isPinnedWindow(w: any): boolean {
  if (w?.route?.params?.pinnedIdentity) return true;
  if (w?.entityId) {
    const entity = entityRegistry.getById(w.entityId);
    if (entity?.entityType === 'AppInstance') return true;
  }
  return false;
}

/**
 * Resolve entity from registry + optional REST fetch. Returns both the entity
 * and a flag indicating whether it came from the local registry (potentially
 * stale CRDT) or a fresh REST fetch.
 */
async function resolveEntityWithSource(entityId: string): Promise<{ entity: any | null; fromRegistry: boolean }> {
  const registryEntity = (entityRegistry.dumpAll().flatMap((s: any) => s.entities) as any[]).find(
    (e: any) => e._id === entityId || e.id === entityId || e.llmId === entityId,
  );

  if (registryEntity) {
    return { entity: registryEntity, fromRegistry: true };
  }

  // Entity not in registry — fetch from REST (authoritative).
  routerStats.restFetches++;
  let fetched: any | null = null;
  try {
    const { entityService } = await import('../services/entityService');
    fetched = await entityService.get(entityId);
  } catch (err) {
    warn('resolveEntity: REST fetch failed', { entityId, err: String(err) });
  }

  if (fetched?._id) {
    try {
      upsertEntity(fetched, { source: 'local', syncCRDT: false, log: false });
    } catch { /* non-fatal */ }
  }

  return { entity: fetched, fromRegistry: false };
}

/** @deprecated Use resolveEntityWithSource for new code. */
async function resolveEntity(entityId: string): Promise<any | null> {
  const { entity } = await resolveEntityWithSource(entityId);
  return entity;
}

/**
 * Derive a human-readable window title from the current route + resolved entity.
 * Priority: app's `getRouteTitle` → entity.name → fallbackTitle →
 * getAppTitleByType.
 */
function deriveTitle(route: WindowRoute, entity: any | null, fallbackTitle?: string): string {
  try {
    const appDefs = useAppRegistryStore.getState?.()?.apps ?? {};
    const def = Object.values(appDefs).find(
      (d: any) => d?.appId === route.appType || d?.component === route.appType,
    ) as any;
    if (def?.getRouteTitle) {
      const custom = def.getRouteTitle(route, entity);
      if (custom) return custom;
    }
  } catch { /* optional, non-fatal */ }

  if (entity?.name) return entity.name;
  if (fallbackTitle) return fallbackTitle;
  return getAppTitleByType(route.appType);
}

// ─── Core: reconcileWindowIdentity ────────────────────────────────────────────

/**
 * After any route write, compute and mirror `entityId`, `entityLlmId`,
 * `appType`, `title` into the window record. This is the ONLY place those
 * fields are written by the router; apps must not write them directly.
 *
 * Emits a DRIFT warning if the existing `window.entityId` doesn't match the
 * route — which means something wrote `entityId` out-of-band.
 */
export async function reconcileWindowIdentity(
  windowId: string,
  route: WindowRoute,
  fallbackTitle?: string,
): Promise<void> {
  const store = getSpaceStore();
  const window = store.windows?.[windowId];
  if (!window) {
    warn('reconcileWindowIdentity: window not found', { windowId });
    return;
  }

  routerStats.reconciles++;

  const routeEntityId = route.params.entityId;
  let entity: any | null = null;
  let resolvedEntityLlmId: string | undefined;
  let entityFromRegistry = false;

  if (routeEntityId) {
    const resolved = await resolveEntityWithSource(routeEntityId);
    entity = resolved.entity;
    entityFromRegistry = resolved.fromRegistry;
    resolvedEntityLlmId = entity?.llmId ?? route.params.entityLlmId;
  }

  // DRIFT detection: warn if entityId was written out-of-band
  if (
    window.entityId &&
    routeEntityId &&
    window.entityId !== routeEntityId
  ) {
    routerStats.drifts++;
    warn('DRIFT: window.entityId differs from route.params.entityId — likely out-of-band write', {
      windowId,
      windowEntityId: window.entityId,
      routeEntityId,
      driftCount: routerStats.drifts,
    });
  }

  // Title derivation strategy:
  // - When the entity came from the local registry it may have a STALE name (the
  //   CRDT/orgYDocStore snapshot might not reflect a recent rename that was saved
  //   to MongoDB but not yet synced to CRDT). In that case, prefer the existing
  //   window.title — which was correctly persisted to CRDT by the subscribeToChanges
  //   listener at rename time.
  // - When the entity came from a fresh REST fetch it is authoritative; use its name.
  // - When the window has no existing title (new association), derive from entity.
  const existingTitle = fallbackTitle || window.title;
  const title = (() => {
    if (entityFromRegistry && existingTitle) {
      // Registry entity may be stale; keep the window's already-saved title rather
      // than overwriting it. The subscribeToChanges listener will update the title
      // reactively once a fresh entity name arrives via REST or user action.
      return existingTitle;
    }
    return deriveTitle(route, entity, existingTitle);
  })();

  const updates: any = {
    route,
    appType: route.appType,
    entityId: routeEntityId,
    entityLlmId: resolvedEntityLlmId,
    title,
  };

  dbg('reconcileWindowIdentity', {
    windowId,
    appType: route.appType,
    entityId: routeEntityId ?? '(none)',
    title,
    mirroredFrom: 'route',
  });

  store._applyLocalWindowUpdate(windowId, updates, {
    source: 'local',
    syncCRDT: true,
    log: false,
  });
}

// ─── Public router API ────────────────────────────────────────────────────────

export const windowRouter = {
  // ── Read ──────────────────────────────────────────────────────────────────

  getRoute(windowId: string): WindowRoute | null {
    const store = getSpaceStore();
    const w = store.windows?.[windowId];
    if (!w) return null;
    const route = w.route ?? deriveRouteFromWindow(w);
    const appType = canonicalizeWindowAppType(route.appType) || route.appType;
    return appType === route.appType ? route : { ...route, appType };
  },

  canGoBack(windowId: string): boolean {
    const store = getSpaceStore();
    const w = store.windows?.[windowId];
    if (!w) return false;
    if (w.routeHistory && w.routeIndex !== undefined) {
      return w.routeIndex > 0;
    }
    const idx = w.navigationIndex ?? (w.navigationHistory?.length ?? 1) - 1;
    return idx > 0;
  },

  canGoForward(windowId: string): boolean {
    const store = getSpaceStore();
    const w = store.windows?.[windowId];
    if (!w) return false;
    if (w.routeHistory && w.routeIndex !== undefined) {
      return w.routeIndex < w.routeHistory.length - 1;
    }
    const hist = w.navigationHistory ?? [];
    const idx = w.navigationIndex ?? hist.length - 1;
    return idx < hist.length - 1;
  },

  // ── Navigate (push history) ────────────────────────────────────────────────

  async navigate(
    windowId: string,
    route: WindowRoute,
    opts?: { replaceHistory?: boolean; fallbackTitle?: string },
  ): Promise<void> {
    const store = getSpaceStore();
    const w = store.windows?.[windowId];

    if (!w) {
      const available = Object.keys(store.windows ?? {});
      warn('navigate: window not found', {
        windowId,
        availableWindows: available.join(', ') || '(none)',
      });
      return;
    }

    // AppInstance windows have a permanent entityId; never navigate them away.
    if (isPinnedWindow(w)) {
      routerStats.pinnedBlocks++;
      warn('navigate: refusing to navigate pinned-identity window', {
        windowId,
        pinnedBlocks: routerStats.pinnedBlocks,
      });
      return;
    }

    const currentRoute = w.route ?? deriveRouteFromWindow(w);
    const replaceHistory = opts?.replaceHistory ?? false;

    // Build new route history
    const prevHistory: WindowRoute[] = w.routeHistory ?? [currentRoute];
    const prevIndex = w.routeIndex ?? (prevHistory.length - 1);
    const truncated = replaceHistory
      ? prevHistory.slice(0, prevIndex) // replace current
      : prevHistory.slice(0, prevIndex + 1);
    let newHistory = [...truncated, route];
    let newIndex = newHistory.length - 1;

    // Cap history to MAX_ROUTE_HISTORY, keeping the most recent entries
    if (newHistory.length > MAX_ROUTE_HISTORY) {
      routerStats.historyTrims++;
      const trim = newHistory.length - MAX_ROUTE_HISTORY;
      newHistory = newHistory.slice(trim);
      newIndex = newHistory.length - 1;
      dbg('navigate: history trimmed', { windowId, trimmed: trim, newLen: newHistory.length });
    }

    // Legacy entity-id string history (backward compat)
    let legacyHistory: string[] = (
      replaceHistory
        ? (w.navigationHistory ?? []).slice(0, w.navigationIndex ?? 0)
        : (w.navigationHistory ?? []).slice(0, (w.navigationIndex ?? -1) + 1)
    );
    if (route.params.entityId) {
      legacyHistory.push(route.params.entityId);
    }
    if (legacyHistory.length > MAX_ROUTE_HISTORY) {
      legacyHistory = legacyHistory.slice(legacyHistory.length - MAX_ROUTE_HISTORY);
    }

    dbg('navigate', {
      windowId,
      from: JSON.stringify(currentRoute),
      to: JSON.stringify(route),
      historyLen: newHistory.length,
      index: newIndex,
      replaceHistory,
    });

    // Clean up transient launcher entity if any
    const oldEntityId = w.entityId;
    if (oldEntityId && oldEntityId !== route.params.entityId) {
      const oldEntity = (entityRegistry.dumpAll().flatMap((s: any) => s.entities) as any[]).find(
        (e: any) => e._id === oldEntityId || e.id === oldEntityId,
      );
      if (oldEntity?.entityType === 'Window' || oldEntity?.entityType === 'OpenDialog') {
        try {
          const { entityActions } = await import('./entityActions');
          await entityActions.softDeleteEntity(oldEntityId);
        } catch { /* non-fatal */ }
      }
    }

    // Write route + history first (sync via _applyLocalWindowUpdate)
    store._applyLocalWindowUpdate(windowId, {
      route,
      routeHistory: newHistory,
      routeIndex: newIndex,
      navigationHistory: legacyHistory,
      navigationIndex: legacyHistory.length - 1,
      applicationState: { ...(w.applicationState ?? {}), isNew: false },
    }, { source: 'local', syncCRDT: true, log: false });

    // Reconcile mirrors (async: resolves entity name from registry/REST)
    await reconcileWindowIdentity(windowId, route, opts?.fallbackTitle);
  },

  // ── Replace (no history push) ──────────────────────────────────────────────

  async replace(
    windowId: string,
    partial: Partial<WindowRoute>,
    opts?: { fallbackTitle?: string },
  ): Promise<void> {
    const store = getSpaceStore();
    const w = store.windows?.[windowId];
    if (!w) {
      warn('replace: window not found', { windowId });
      return;
    }

    const currentRoute = w.route ?? deriveRouteFromWindow(w);
    const newRoute: WindowRoute = {
      appType: partial.appType ?? currentRoute.appType,
      params: { ...currentRoute.params, ...(partial.params ?? {}) },
    };

    // Respect pinnedIdentity: block any entityId write (including clears where
    // entityId: undefined is explicitly passed) on pinned-identity windows.
    // Using 'entityId' in (partial.params ?? {}) catches both sets and clears.
    if (isPinnedWindow(w) && 'entityId' in (partial.params ?? {})) {
      routerStats.pinnedBlocks++;
      warn('replace: refusing to touch entityId on pinned-identity window', {
        windowId,
        pinnedBlocks: routerStats.pinnedBlocks,
      });
      newRoute.params.entityId = currentRoute.params.entityId;
      newRoute.params.entityLlmId = currentRoute.params.entityLlmId;
    }

    // Track entityId clears for observability
    if ('entityId' in (partial.params ?? {}) && !partial.params?.entityId) {
      routerStats.clears++;
      dbg('replace: entityId cleared', { windowId, clears: routerStats.clears });
    }

    dbg('replace', { windowId, from: JSON.stringify(currentRoute), to: JSON.stringify(newRoute) });

    // Update route in current history slot
    const history = w.routeHistory ? [...w.routeHistory] : [currentRoute];
    const idx = w.routeIndex ?? history.length - 1;
    history[idx] = newRoute;

    store._applyLocalWindowUpdate(windowId, {
      route: newRoute,
      routeHistory: history,
      routeIndex: idx,
    }, { source: 'local', syncCRDT: true, log: false });

    await reconcileWindowIdentity(windowId, newRoute, opts?.fallbackTitle);
  },

  // ── setParams (merge params, no history) ──────────────────────────────────

  async setParams(
    windowId: string,
    params: Partial<WindowRouteParams>,
    opts?: { fallbackTitle?: string },
  ): Promise<void> {
    return this.replace(windowId, { params }, opts);
  },

  // ── Back / Forward ────────────────────────────────────────────────────────

  async back(windowId: string): Promise<void> {
    const store = getSpaceStore();
    const w = store.windows?.[windowId];
    if (!w) return;

    const history = w.routeHistory;
    const idx = w.routeIndex;

    if (history && idx !== undefined && idx > 0) {
      const newIdx = idx - 1;
      const route = history[newIdx];

      dbg('back', { windowId, fromIndex: idx, toIndex: newIdx });

      store._applyLocalWindowUpdate(windowId, {
        route,
        routeIndex: newIdx,
        navigationIndex: newIdx,
      }, { source: 'local', syncCRDT: true, log: false });

      await reconcileWindowIdentity(windowId, route);
      return;
    }

    // Legacy fallback
    const legacyHistory = w.navigationHistory;
    const legacyIdx = w.navigationIndex ?? (legacyHistory?.length ?? 1) - 1;
    if (!legacyHistory || legacyIdx <= 0) {
      warn('back: already at beginning of history', { windowId });
      return;
    }
    const newIdx = legacyIdx - 1;
    const entityId = legacyHistory[newIdx];

    dbg('back (legacy)', { windowId, fromIndex: legacyIdx, toIndex: newIdx, entityId });

    const entity = (entityRegistry.dumpAll().flatMap((s: any) => s.entities) as any[]).find(
      (e: any) => e._id === entityId || e.id === entityId,
    );
    if (!entity) { warn('back: entity not found in history', { windowId, entityId }); return; }

    const { getAppTypeForEntity } = await import('../utils/entityTypeMapping');
    const appType = getAppTypeForEntity(entity);
    const legacyRoute: WindowRoute = { appType, params: { entityId, entityLlmId: entity.llmId } };

    store._applyLocalWindowUpdate(windowId, {
      route: legacyRoute,
      navigationIndex: newIdx,
    }, { source: 'local', syncCRDT: true, log: false });
    await reconcileWindowIdentity(windowId, legacyRoute);
  },

  async forward(windowId: string): Promise<void> {
    const store = getSpaceStore();
    const w = store.windows?.[windowId];
    if (!w) return;

    const history = w.routeHistory;
    const idx = w.routeIndex;

    if (history && idx !== undefined && idx < history.length - 1) {
      const newIdx = idx + 1;
      const route = history[newIdx];

      dbg('forward', { windowId, fromIndex: idx, toIndex: newIdx });

      store._applyLocalWindowUpdate(windowId, {
        route,
        routeIndex: newIdx,
        navigationIndex: newIdx,
      }, { source: 'local', syncCRDT: true, log: false });

      await reconcileWindowIdentity(windowId, route);
      return;
    }

    // Legacy fallback
    const legacyHistory = w.navigationHistory;
    const legacyIdx = w.navigationIndex ?? (legacyHistory?.length ?? 1) - 1;
    if (!legacyHistory || legacyIdx >= legacyHistory.length - 1) {
      warn('forward: already at end of history', { windowId });
      return;
    }
    const newIdx = legacyIdx + 1;
    const entityId = legacyHistory[newIdx];

    const entity = (entityRegistry.dumpAll().flatMap((s: any) => s.entities) as any[]).find(
      (e: any) => e._id === entityId || e.id === entityId,
    );
    if (!entity) { warn('forward: entity not found', { windowId, entityId }); return; }

    const { getAppTypeForEntity } = await import('../utils/entityTypeMapping');
    const appType = getAppTypeForEntity(entity);
    const legacyRoute: WindowRoute = { appType, params: { entityId, entityLlmId: entity.llmId } };

    store._applyLocalWindowUpdate(windowId, {
      route: legacyRoute,
      navigationIndex: newIdx,
    }, { source: 'local', syncCRDT: true, log: false });
    await reconcileWindowIdentity(windowId, legacyRoute);
  },

  // ── openEntity — focus-or-create with full dedup semantics ────────────────

  /**
   * Open an entity in the best available window. Replicates the dedup logic
   * from space_open_window / space_openEntity / createWindow:
   *  - Both llmId + entityId must match for entities with llmId.
   *  - Wrong appType → close stale + recreate.
   *  - AppInstance: match by appType + entityId (same window).
   * Returns the windowId of the focused or newly-created window.
   */
  async openEntity(
    entityOrId: any | string,
    opts: {
      spaceId?: string;
      mode?: WindowRouteParams['mode'];
      params?: Partial<WindowRouteParams>;
      newWindow?: boolean;
      position?: { x: number; y: number };
      size?: { width: number; height: number };
    } = {},
  ): Promise<string> {
    const store = getSpaceStore();

    // Resolve entity
    let entity: any;
    if (typeof entityOrId === 'string') {
      entity = await resolveEntity(entityOrId);
      if (!entity) throw new Error(`[windowRouter] openEntity: entity ${entityOrId} not found`);
    } else {
      entity = entityOrId;
      // Ensure it's in the registry
      if (entity?._id) {
        const alreadyIn = (entityRegistry.dumpAll().flatMap((s: any) => s.entities) as any[]).some(
          (e: any) => e._id === entity._id,
        );
        if (!alreadyIn) upsertEntity(entity, { source: 'local', syncCRDT: false, log: false });
      }
    }

    const targetEntityId: string = entity._id || entity.id;
    const targetLlmId: string | undefined = entity.llmId;

    const { getAppTypeForEntity } = await import('../utils/entityTypeMapping');
    const appType = getAppTypeForEntity(entity);

    dbg('openEntity', { entityId: targetEntityId, appType, mode: opts.mode });

    if (!opts.newWindow) {
      // Look for an existing window showing this entity. appType must match too:
      // some windows (e.g. the Drive browser) mirror their "currently selected
      // entity" onto entityId/entityLlmId for route-sync purposes without actually
      // being a dedicated viewer for that entity — matching (and closing!) purely
      // on identity would destroy that unrelated window instead of opening a new one.
      const windows = store.windows ?? {};
      const existing = Object.entries(windows).find(([, w]: any) => {
        const win = w as any;
        const identityMatch = targetLlmId
          ? win.entityLlmId === targetLlmId && win.entityId === targetEntityId
          : win.entityId === targetEntityId;
        return identityMatch && canonicalizeWindowAppType(win.appType) === appType;
      });

      if (existing) {
        const [existingWindowId] = existing as [string, any];
        dbg('openEntity: focusing existing window', { existingWindowId });
        store.bringWindowToFront(existingWindowId);
        return existingWindowId;
      }

      // AppInstance fallback
      if (entity.entityType === 'AppInstance') {
        const instanceWindow = Object.entries(windows).find(([, w]: any) =>
          canonicalizeWindowAppType(w.appType) === appType && (w.entityId === targetEntityId || !w.entityId),
        );
        if (instanceWindow) {
          const [wId] = instanceWindow;
          store.bringWindowToFront(wId);
          return wId;
        }
      }
    }

    // Create new window
    const mode = opts.mode;
    const windowId = await store.actions.createWindow({
      appType,
      entity,
      position: opts.position,
      size: opts.size,
      props: mode ? { renderMode: mode } : undefined,
    });

    // Build route with pinnedIdentity for AppInstance entities
    const route: WindowRoute = {
      appType,
      params: {
        entityId: targetEntityId,
        entityLlmId: targetLlmId,
        ...(mode ? { mode } : {}),
        ...(entity.entityType === 'AppInstance' ? { pinnedIdentity: true } : {}),
        ...(opts.params ?? {}),
      },
    };
    await this.navigate(windowId, route, { replaceHistory: true });

    store.bringWindowToFront(windowId);

    // Track entity/app opened via windowRouter (covers Drive double-click, agent open, etc.)
    try {
      const { analytics: _analytics } = await import('../services/analytics/analytics');
      _analytics.track('app_opened', {
        app_name: appType,
        source: 'window_router',
      });
    } catch { /* non-fatal */ }

    return windowId;
  },

  // ── openApp — focus-or-create singleton/app window ──────────────────────

  async openApp(
    appType: string,
    params?: Partial<WindowRouteParams>,
    opts?: {
      title?: string;
      position?: { x: number; y: number };
      size?: { width: number; height: number };
      newWindow?: boolean;
    },
  ): Promise<string> {
    const store = getSpaceStore();

    dbg('openApp', { appType, params });

    if (!opts?.newWindow) {
      const existing = Object.entries(store.windows ?? {}).find(
        ([, w]: any) => canonicalizeWindowAppType(w.appType) === appType,
      );
      if (existing) {
        const [windowId] = existing;
        store.bringWindowToFront(windowId);
        return windowId;
      }
    }

    const windowId = await store.actions.createWindow({
      appType,
      title: opts?.title ?? getAppTitleByType(appType),
      position: opts?.position,
      size: opts?.size,
    });

    const route: WindowRoute = { appType, params: params ?? {} };
    await this.navigate(windowId, route, { replaceHistory: true });

    store.bringWindowToFront(windowId);
    return windowId;
  },
};

// ─── Global debug surface ─────────────────────────────────────────────────────

if (typeof window !== 'undefined') {
  (window as any).__windowRouter = {
    dump() {
      const store = getSpaceStore();
      const windows = store.windows ?? {};
      return Object.fromEntries(
        Object.entries(windows).map(([id, w]: any) => [
          id,
          {
            appType: w.appType,
            route: w.route ?? deriveRouteFromWindow(w),
            routeHistory: (w.routeHistory ?? []).map((r: any) => `${r.appType}|${r.params?.entityId ?? ''}`),
            routeIndex: w.routeIndex ?? 0,
            entityId: w.entityId,
            title: w.title,
            pinned: isPinnedWindow(w),
          },
        ]),
      );
    },
    route(windowId: string) {
      return windowRouter.getRoute(windowId);
    },
    history(windowId: string) {
      const store = getSpaceStore();
      const w = store.windows?.[windowId];
      return w?.routeHistory ?? [];
    },
    stats: routerStats,
    /**
     * Invariant check: every open window's route.params.entityId should equal
     * window.entityId, and no AppInstance window should have a missing/cleared
     * entityId. Logs violations; returns the violation list for programmatic use.
     */
    assert() {
      const store = getSpaceStore();
      const windows = store.windows ?? {};
      const violations: string[] = [];

      for (const [id, w] of Object.entries(windows) as [string, any][]) {
        const routeEid = w.route?.params?.entityId;
        const windowEid = w.entityId;

        // route ↔ entityId mismatch (post-reconcile should always be in sync)
        if (routeEid !== windowEid) {
          violations.push(`[${id}] entityId mismatch: route=${routeEid ?? 'undefined'} window=${windowEid ?? 'undefined'}`);
        }

        // AppInstance identity cleared
        if (isPinnedWindow(w) && !windowEid) {
          violations.push(`[${id}] pinned window has no entityId — identity was cleared`);
        }

        // History within cap
        if ((w.routeHistory?.length ?? 0) > MAX_ROUTE_HISTORY) {
          violations.push(`[${id}] routeHistory.length=${w.routeHistory.length} exceeds MAX_ROUTE_HISTORY=${MAX_ROUTE_HISTORY}`);
        }
      }

      if (violations.length === 0) {
        console.info('[WINDOW_ROUTER] assert: ✅ all invariants satisfied');
      } else {
        console.warn('[WINDOW_ROUTER] assert: ❌ violations found:', violations);
      }
      return violations;
    },
    navigate: windowRouter.navigate.bind(windowRouter),
    replace: windowRouter.replace.bind(windowRouter),
    back: windowRouter.back.bind(windowRouter),
    forward: windowRouter.forward.bind(windowRouter),
    canGoBack: windowRouter.canGoBack.bind(windowRouter),
    canGoForward: windowRouter.canGoForward.bind(windowRouter),
  };
}
